/**
 * SOPBottlenecksPanel — surfaces which steps get skipped/rejected most,
 * which SOPs take longest end-to-end, and top assignees by throughput.
 * Fed by GET /api/v1/workspaces/{ws}/sop-analytics?days=N.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertTriangle, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function SOPBottlenecksPanel({ wsId }) {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/workspaces/${wsId}/sop-analytics?days=${days}`);
        setData(d);
      } catch (e) { toast.error(apiErrorMessage(e, "Analytics failed")); }
    })();
  }, [wsId, days]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="space-y-4" data-testid="sop-bottlenecks">
      <div className="flex items-center gap-2">
        <span className="label-eyebrow text-[10px]">Window</span>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid="sop-bn-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-[11px] text-muted-foreground">
          {data.totals.runs} runs · {data.totals.completed} completed · avg {data.totals.avg_hours}h end-to-end
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Section title="Step bottlenecks (skip + reject)" icon={AlertTriangle} tone="danger" testKey="steps">
          {data.step_bottlenecks.length === 0 ? <Empty note="Not enough data yet." /> :
            data.step_bottlenecks.map((s) => (
              <div key={s.step_id} className="px-3 py-2 border-b border-border last:border-0" data-testid={`sop-bn-step-${s.step_id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono w-8 shrink-0 text-red-600">{s.issue_pct}%</span>
                  <span className="text-sm truncate flex-1">{s.step_title}</span>
                </div>
                <div className="text-[10px] text-muted-foreground ml-10">
                  {s.sop_title} · {s.step_kind} · {s.total} runs · skip {s.skip_rate_pct}% · reject {s.reject_rate_pct}%
                </div>
                <div className="ml-10 h-1.5 bg-muted mt-0.5 relative overflow-hidden">
                  <div className="h-full bg-red-500 absolute left-0 top-0" style={{ width: `${Math.min(100, s.issue_pct)}%` }} />
                </div>
              </div>
            ))}
        </Section>

        <Section title="Slowest SOPs end-to-end" icon={TrendingUp} tone="warn" testKey="throughput">
          {data.sop_throughput.length === 0 ? <Empty note="No completed runs yet." /> :
            data.sop_throughput.map((t) => (
              <div key={t.sop_id} className="px-3 py-2 border-b border-border last:border-0" data-testid={`sop-bn-sop-${t.sop_id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono w-12 shrink-0 text-amber-600">
                    {t.avg_completion_hours != null ? `${t.avg_completion_hours}h` : "—"}
                  </span>
                  <span className="text-sm truncate flex-1">{t.sop_title}</span>
                </div>
                <div className="text-[10px] text-muted-foreground ml-14">
                  {t.runs} runs · {t.completion_rate_pct}% completed · {t.in_progress} in progress · {t.cancelled} cancelled
                </div>
              </div>
            ))}
        </Section>

        <Section title="Top assignees" icon={Users} testKey="assignees">
          {data.top_assignees.length === 0 ? <Empty note="No assigned runs yet." /> :
            data.top_assignees.map((u) => (
              <div key={u.user_id} className="px-3 py-2 border-b border-border last:border-0" data-testid={`sop-bn-user-${u.user_id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm truncate flex-1 font-semibold">
                    {u.user?.name || u.user?.email || u.user_id.slice(0, 8) + "…"}
                  </span>
                  <span className="font-mono text-xs">{u.completed}/{u.runs}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {u.completion_rate_pct}% completion · avg {u.avg_completion_hours != null ? `${u.avg_completion_hours}h` : "—"}
                </div>
              </div>
            ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, tone = "ok", testKey, children }) {
  const toneCls = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-foreground";
  return (
    <div className="border border-border" data-testid={`sop-bn-section-${testKey}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border">
        <Icon className={`w-4 h-4 ${toneCls}`} />
        <span className="text-sm font-bold">{title}</span>
      </div>
      <div className="max-h-96 overflow-auto">{children}</div>
    </div>
  );
}

function Empty({ note }) {
  return <div className="p-4 text-xs text-muted-foreground text-center">{note}</div>;
}
